import { useState, useCallback, useEffect, useRef } from 'react'
import {
  Check, Zap, Loader2, X, AlertTriangle, RefreshCw, Wifi, WifiOff,
} from 'lucide-react'
import { InlineWebhookPanel } from './components/InlineWebhookPanel'
import {
  inputCls, labelCls, SectionLabel, CopyButton, RawJsonCollapsible, ComboSelect,
  EndpointPicker, PayloadEditor, CurlCollapsible,
} from './components/shared'
import type { EndpointDef } from './components/shared'
import { KYB_SCENARIOS, DEFAULT_CREDS } from './data'
import { proxyUrl, buildHeaders, outcomeColors } from './utils'
import type { Creds, LogEntry } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = Record<string, any>

const KYB_V2_ENDPOINTS: EndpointDef[] = [
  { id: 'lookup', method: 'POST', label: 'Lookup Organization', path: '/organizations/lookup', category: 'Entity Management', core: true },
  { id: 'create', method: 'POST', label: 'Create Organization', path: '/organizations', category: 'Entity Management', core: true },
  { id: 'execute', method: 'POST', label: 'Execute Workflow', path: '/organizations/{entityId}/serviceprofiles/{service}/workflows/{workflow}/execute', category: 'Workflows', core: true },
  { id: 'execute-token', method: 'POST', label: 'Execute by Token', path: '/organizations/workflows/{workflow}/execute', description: 'Skip create step', category: 'Workflows', core: true },
  { id: 'get-result', method: 'GET', label: 'Workflow Results', path: '/organizations/{entityId}/serviceprofiles/{service}/workflows/{workflow}/executions/{executionId}', category: 'Workflows', core: true },
  { id: 'get-entity', method: 'GET', label: 'Entity Details', path: '/organizations/{entityId}', category: 'Entity Management', core: true },
  // Entity Management
  { id: 'update-org', method: 'PATCH', label: 'Update Organization', path: '/organizations/{entityId}', category: 'Entity Management' },
  { id: 'delete-org', method: 'DELETE', label: 'Delete Organization', path: '/organizations/{entityId}', category: 'Entity Management' },
  // Ownership
  { id: 'ownership', method: 'POST', label: 'Organization Ownership', path: '/organizations/ownership', category: 'Ownership' },
  // Workflows
  { id: 'list-executions', method: 'GET', label: 'List Executions', path: '/organizations/{entityId}/serviceprofiles/{service}/workflows/{workflow}/executions', category: 'Workflows' },
  // Profiles
  { id: 'get-profile', method: 'GET', label: 'Get Service Profile', path: '/organizations/{entityId}/serviceprofiles/{service}', category: 'Profiles' },
  { id: 'update-profile', method: 'PATCH', label: 'Update Service Profile', path: '/organizations/{entityId}/serviceprofiles/{service}', category: 'Profiles' },
  // Results
  { id: 'get-results', method: 'GET', label: 'Get Process Results', path: '/organizations/{entityId}/results', category: 'Results' },
  { id: 'get-result-single', method: 'GET', label: 'Get Single Result', path: '/organizations/{entityId}/results/{resultId}', category: 'Results' },
  { id: 'update-aml-status', method: 'PATCH', label: 'Update AML Status', path: '/organizations/{entityId}/results/aml', category: 'Results' },
  // Documents
  { id: 'get-documents', method: 'GET', label: 'Get All Documents', path: '/organizations/{entityId}/documents', category: 'Documents' },
  { id: 'create-document', method: 'POST', label: 'Create Document', path: '/organizations/{entityId}/documents', category: 'Documents' },
  { id: 'get-document', method: 'GET', label: 'Get Document', path: '/organizations/{entityId}/documents/{documentId}', category: 'Documents' },
  { id: 'update-document', method: 'PATCH', label: 'Update Document', path: '/organizations/{entityId}/documents/{documentId}', category: 'Documents' },
  { id: 'delete-document', method: 'DELETE', label: 'Delete Document', path: '/organizations/{entityId}/documents/{documentId}', category: 'Documents' },
  // Relationships
  { id: 'get-relationships', method: 'GET', label: 'Get Relationships', path: '/organizations/{entityId}/relationships', category: 'Relationships' },
  { id: 'update-relationships', method: 'PUT', label: 'Add/Update Relationships', path: '/organizations/{entityId}/relationships', category: 'Relationships' },
  { id: 'delete-relationships', method: 'DELETE', label: 'Delete Relationships', path: '/organizations/{entityId}/relationships', category: 'Relationships' },
  // Monitoring
  { id: 'toggle-monitoring', method: 'PATCH', label: 'Toggle Monitoring', path: '/organizations/{entityId}/monitoring', category: 'Monitoring' },
  // Hosted URL
  { id: 'generate-hosted-url', method: 'POST', label: 'Generate Hosted URL', path: '/organizations/hostedUrl', category: 'Hosted URL' },
]

// ── Entity result panel ────────────────────────────────────────────────────────

function EntityResultPanel({ entityResult }: { entityResult: unknown }) {
  try {
    const raw = entityResult as AnyObj
    const org = raw?.organization ?? raw
    const linked: Record<string, AnyObj> = org?.linkedIndividuals ?? {}
    function resolvedName(eid: string): string {
      const ind = linked[eid]
      const fullName = `${ind?.name?.givenName ?? ''} ${ind?.name?.familyName ?? ''}`.trim()
      return ind?.name?.displayName ?? (fullName || (eid.slice(0, 8) + '…'))
    }
    const ubos: AnyObj[] = Array.isArray(org?.ultimateBeneficialOwners) ? org.ultimateBeneficialOwners : []
    const officials: AnyObj[] = Array.isArray(org?.officials) ? org.officials : []
    const shareholders: AnyObj[] = Array.isArray(org?.shareholders) ? org.shareholders : []
    const noOwnership = ubos.length === 0 && officials.length === 0 && shareholders.length === 0
    const legalName = org?.details?.name?.name ?? org?.details?.name ?? org?.name?.name
    const statusCode = org?.details?.status?.normalized?.code as string | undefined

    return (
      <div className="rounded-[11px] overflow-hidden" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
        <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #1a2740', background: '#0a1322' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">GET /organizations — entity profile</p>
        </div>
        <div className="p-4 space-y-2 text-[10px]">
          {org?.entityId && <div className="flex gap-2"><span className="text-[#5b6b85] font-mono w-28 shrink-0">entityId:</span><span className="text-[#94a3b8] font-mono">{String(org.entityId)}</span></div>}
          {legalName && <div className="flex gap-2"><span className="text-[#5b6b85] font-mono w-28 shrink-0">Legal Name:</span><span className="text-[#94a3b8] font-mono">{String(legalName)}</span></div>}
          {statusCode && <div className="flex gap-2"><span className="text-[#5b6b85] font-mono w-28 shrink-0">Status:</span><span className="font-mono font-semibold" style={{ color: statusCode === 'ACTV' ? '#34d399' : '#fb923c' }}>{statusCode}</span></div>}
          {ubos.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-emerald-400 mb-1">UBOs ({ubos.length})</p>
              {ubos.map((u, i) => (
                <div key={i} className="flex items-center gap-2 py-0.5 border-t text-[#94a3b8] font-mono" style={{ borderColor: '#1a2740' }}>
                  <span className="flex-1">{resolvedName(u?.entityId ?? '')}</span>
                  {u?.percentageOwned?.total != null && <span className="text-emerald-400">{u.percentageOwned.total}%</span>}
                </div>
              ))}
            </div>
          )}
          {officials.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-sky-400 mb-1">Officials / Directors ({officials.length})</p>
              {officials.map((o, i) => (
                <div key={i} className="flex items-center gap-2 py-0.5 border-t text-[#94a3b8] font-mono" style={{ borderColor: '#1a2740' }}>
                  <span className="flex-1">{resolvedName(o?.entityId ?? '')}</span>
                  {o?.role?.description && <span className="text-sky-400 text-[9px]">{String(o.role.description)}</span>}
                </div>
              ))}
            </div>
          )}
          {shareholders.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-violet-400 mb-1">Shareholders ({shareholders.length})</p>
              {shareholders.map((s, i) => (
                <div key={i} className="flex items-center gap-2 py-0.5 border-t text-[#94a3b8] font-mono" style={{ borderColor: '#1a2740' }}>
                  <span className="flex-1">{resolvedName(s?.entityId ?? '')}</span>
                  {s?.totalShares != null && <span className="text-violet-400">{s.totalShares} shares</span>}
                </div>
              ))}
            </div>
          )}
          {noOwnership && <p className="text-[#475569] italic">No ownership data yet — run a workflow first</p>}
        </div>
      </div>
    )
  } catch {
    return (
      <div className="rounded-[11px] p-4" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
        <pre className="text-[9px] text-[#94a3b8] font-mono whitespace-pre-wrap break-all max-h-48 overflow-y-auto">{JSON.stringify(entityResult, null, 2)}</pre>
      </div>
    )
  }
}

// ── Main component ─────────────────────────────────────────────────────────────

interface KybV2WorkspaceProps {
  creds: Creds
  addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void
}

export function KybV2Workspace({ creds, addLog }: KybV2WorkspaceProps) {
  const apiBase = creds.apiBase || DEFAULT_CREDS.apiBase
  const hasCreds = !!(creds.customerId && creds.apiKey)

  const [selectedEndpoint, setSelectedEndpoint] = useState<string>('lookup')
  const [connStatus, setConnStatus] = useState<'idle' | 'ok' | 'error'>('idle')
  const [workflows, setWorkflows] = useState<{ serviceName: string; workflowName: string }[]>([])
  const [selectedWorkflow, setSelectedWorkflow] = useState('')
  const [kybServiceName, setKybServiceName] = useState('KYB')
  const [scenarioIdx, setScenarioIdx] = useState<number | null>(null)
  const [searchBy, setSearchBy] = useState<'name' | 'registration'>('registration')
  const [searchName, setSearchName] = useState('')
  const [searchRegNum, setSearchRegNum] = useState('')
  const [searchRegType, setSearchRegType] = useState('ACN')
  const [searchCountry, setSearchCountry] = useState('AUS')
  const [lookupResults, setLookupResults] = useState<AnyObj[]>([])
  const [selectedOrg, setSelectedOrg] = useState<AnyObj | null>(null)
  const [entityId, setEntityId] = useState<string | null>(null)
  const [getEntityIdInput, setGetEntityIdInput] = useState('')
  const [getExecutionIdInput, setGetExecutionIdInput] = useState('')
  const [workflowExecutionId, setWorkflowExecutionId] = useState<string | null>(null)
  const [loading, setLoading] = useState<string | null>(null)
  const [workflowResult, setWorkflowResult] = useState<AnyObj | null>(null)
  const [resultOrg, setResultOrg] = useState<AnyObj | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)
  const [entityResult, setEntityResult] = useState<unknown>(null)

  // PayloadEditor state
  const [editorText, setEditorText] = useState('')
  const [editorDirty, setEditorDirty] = useState(false)
  const [editorError, setEditorError] = useState<string | null>(null)

  const epDef = KYB_V2_ENDPOINTS.find(e => e.id === selectedEndpoint) ?? KYB_V2_ENDPOINTS[0]
  const isGetEndpoint = epDef.method === 'GET'

  // Derive payload based on selected endpoint
  function derivedPayload(): Record<string, unknown> {
    if (selectedEndpoint === 'lookup') {
      if (searchBy === 'name') {
        return { organizationName: searchName, region: { country: searchCountry } }
      }
      return { organizationNumber: { registrationNumber: searchRegNum, registryCode: searchRegType }, region: { country: searchCountry } }
    }
    if (selectedEndpoint === 'create') {
      return { organizationToken: selectedOrg?.organizationToken ?? '' }
    }
    if (selectedEndpoint === 'execute') {
      return { serviceName: kybServiceName, workflowName: selectedWorkflow }
    }
    if (selectedEndpoint === 'execute-token') {
      return { organizationToken: selectedOrg?.organizationToken ?? '' }
    }
    return {}
  }

  function dynamicPath(): string {
    const eid = entityId ?? getEntityIdInput
    const weid = workflowExecutionId ?? getExecutionIdInput
    return epDef.path
      .replace('{entityId}', eid || '{entityId}')
      .replace('{service}', kybServiceName || '{service}')
      .replace('{workflow}', selectedWorkflow || '{workflow}')
      .replace('{executionId}', weid || '{executionId}')
  }

  const fullUrl = `${apiBase}${dynamicPath()}`

  // Sync derived payload to editor (unless user edited)
  const derivedJson = JSON.stringify(derivedPayload())
  useEffect(() => {
    if (!editorDirty) {
      try {
        setEditorText(JSON.stringify(JSON.parse(derivedJson), null, 2))
      } catch {
        setEditorText(derivedJson)
      }
      setEditorError(null)
    }
  }, [derivedJson, editorDirty])

  // Auto-populate GET inputs from prior results
  useEffect(() => {
    if (entityId && !getEntityIdInput) setGetEntityIdInput(entityId)
  }, [entityId, getEntityIdInput])

  useEffect(() => {
    if (workflowExecutionId && !getExecutionIdInput) setGetExecutionIdInput(workflowExecutionId)
  }, [workflowExecutionId, getExecutionIdInput])

  function handleEndpointSelect(id: string) {
    setSelectedEndpoint(id)
    setEditorDirty(false)
    setEditorError(null)
  }

  function handleEditorChange(v: string) { setEditorText(v); setEditorDirty(true); setEditorError(null) }
  function resetEditor() { setEditorDirty(false); setEditorText(JSON.stringify(derivedPayload(), null, 2)); setEditorError(null) }
  function getEditorPayload(): Record<string, unknown> | null {
    try { return JSON.parse(editorText) as Record<string, unknown> }
    catch { setEditorError('Invalid JSON — fix syntax before sending'); return null }
  }

  const doFetch = useCallback(async (method: string, path: string, body: unknown | undefined, annotation: string) => {
    const url = `${apiBase}${path}`
    const start = Date.now()
    const res = await fetch(proxyUrl(path), {
      method,
      headers: buildHeaders(creds, method !== 'GET'),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
    const durationMs = Date.now() - start
    let data: unknown
    try { data = await res.json() } catch { data = {} }
    addLog({ method, url, status: res.status, durationMs, requestBody: body, responseBody: data, annotation })
    if (!res.ok) throw new Error((data as { error?: string })?.error ?? res.statusText)
    return data
  }, [apiBase, creds, addLog])

  const loadWorkflows = useCallback(async () => {
    setLoading('wf')
    try {
      const data = await doFetch('GET', '/workflows', undefined, 'List configured KYB workflows') as { workflows?: { serviceName: string; workflowName: string }[] }
      const all = data?.workflows ?? []
      const kyb = all.filter(w => w.serviceName?.toUpperCase().includes('KYB'))
      const toShow = kyb.length > 0 ? kyb : all
      setWorkflows(toShow)
      if (toShow.length > 0) {
        setSelectedWorkflow(toShow[0].workflowName)
        setKybServiceName(toShow[0].serviceName ?? 'KYB')
      }
    } catch { /* logged */ } finally { setLoading(null) }
  }, [doFetch])

  const lastAutoLoadedCustomer = useRef('')
  useEffect(() => {
    if (hasCreds && creds.customerId !== lastAutoLoadedCustomer.current) {
      lastAutoLoadedCustomer.current = creds.customerId
      void loadWorkflows()
    }
  }, [hasCreds, creds.customerId, loadWorkflows])

  async function testConnection() {
    setLoading('conn')
    try { await doFetch('GET', '/kyc/ruok', undefined, 'Health check'); setConnStatus('ok') }
    catch { setConnStatus('error') } finally { setLoading(null) }
  }

  async function lookupBusiness() {
    setLoading('lookup')
    const payload = getEditorPayload() ?? derivedPayload()
    const body = searchBy === 'name'
      ? { organizationName: searchName, region: { country: searchCountry } }
      : { organizationNumber: { registrationNumber: searchRegNum, registryCode: searchRegType }, region: { country: searchCountry } }
    const finalBody = editorDirty ? payload : body
    try {
      const data = await doFetch('POST', '/organizations/lookup', finalBody, 'Search business registry (ABR/ASIC)') as { matchedOrganizations?: AnyObj[] }
      setLookupResults(data?.matchedOrganizations ?? [])
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function createOrganization() {
    const payload = getEditorPayload()
    if (!payload) return
    setLoading('createOrg')
    try {
      const data = await doFetch('POST', '/organizations', payload, 'Create organisation entity from lookup token') as { organization?: { entityId?: string }; entityId?: string }
      const eid = data?.organization?.entityId ?? data?.entityId
      if (eid) { setEntityId(eid); setGetEntityIdInput(eid) }
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function executeForEntity() {
    if (!selectedWorkflow) return
    const eid = entityId ?? getEntityIdInput
    if (!eid) return
    const payload = getEditorPayload()
    if (!payload) return
    setLoading('execEntity')
    try {
      const data = await doFetch('POST', `/organizations/${eid}/serviceprofiles/${kybServiceName}/workflows/${selectedWorkflow}/execute`, payload, 'Execute KYB workflow — returns 202 async') as { workflowExecutionId?: string }
      if (data?.workflowExecutionId) { setWorkflowExecutionId(data.workflowExecutionId); setGetExecutionIdInput(data.workflowExecutionId) }
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function executeByToken() {
    if (!selectedWorkflow) return
    const payload = getEditorPayload()
    if (!payload) return
    setLoading('execToken')
    try {
      const data = await doFetch('POST', `/organizations/workflows/${selectedWorkflow}/execute`, payload, 'Execute KYB workflow by token — skips create step') as { entityId?: string; workflowExecutionId?: string }
      if (data?.entityId) { setEntityId(data.entityId); setGetEntityIdInput(data.entityId) }
      if (data?.workflowExecutionId) { setWorkflowExecutionId(data.workflowExecutionId); setGetExecutionIdInput(data.workflowExecutionId) }
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function getResult() {
    const eid = entityId ?? getEntityIdInput
    const weid = workflowExecutionId ?? getExecutionIdInput
    if (!eid || !weid) return
    setLoading('result')
    try {
      const data = await doFetch('GET', `/organizations/${eid}/serviceprofiles/${kybServiceName}/workflows/${selectedWorkflow}/executions/${weid}`, undefined, 'Get KYB workflow execution result') as { workflowResult?: AnyObj; organization?: AnyObj }
      if (data?.workflowResult) {
        setWorkflowResult(data.workflowResult)
        setResultOrg(data.organization ?? null)
        setFullResult(data)
      }
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function getEntity() {
    const eid = entityId ?? getEntityIdInput
    if (!eid) return
    setLoading('entity')
    try {
      const data = await doFetch('GET', `/organizations/${eid}`, undefined, 'Get organisation entity — enriched profile')
      setEntityResult(data)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  function applyScenario(idx: number) {
    const s = KYB_SCENARIOS[idx]
    setSearchBy('registration')
    setSearchRegType(s.searchRegType)
    setSearchRegNum(s.searchValue)
    setSearchName(s.name)
    setLookupResults([]); setSelectedOrg(null)
  }

  function reset() {
    setEntityId(null); setWorkflowExecutionId(null); setWorkflowResult(null)
    setResultOrg(null); setFullResult(null); setEntityResult(null)
    setLookupResults([]); setSelectedOrg(null)
    setGetEntityIdInput(''); setGetExecutionIdInput('')
  }

  const outcome = workflowResult ? (() => {
    const wr = workflowResult
    const s = (wr.workflowExecutionState ?? wr.overallRiskLevel ?? '').toUpperCase()
    if (wr.issues?.length || s === 'TIMEOUT' || s === 'ERROR') return 'FAIL'
    const rs = (wr.checkSummary?.overallRiskLevel ?? wr.riskAssessment?.riskLevel ?? s)
    if (rs === 'LOW' || rs === 'CLEAR' || rs === 'PASS') return 'PASS'
    if (rs === 'MEDIUM' || rs === 'REFER' || rs === 'REVIEW') return 'REFER'
    if (rs === 'HIGH' || rs === 'FAIL') return 'FAIL'
    return s || 'DONE'
  })() : ''
  const oc = outcomeColors(outcome)

  // Determine run action and disabled state per endpoint
  function handleRun() {
    if (selectedEndpoint === 'lookup') void lookupBusiness()
    else if (selectedEndpoint === 'create') void createOrganization()
    else if (selectedEndpoint === 'execute') void executeForEntity()
    else if (selectedEndpoint === 'execute-token') void executeByToken()
    else if (selectedEndpoint === 'get-result') void getResult()
    else if (selectedEndpoint === 'get-entity') void getEntity()
  }

  function runDisabled(): boolean {
    if (!hasCreds || loading !== null) return true
    if (selectedEndpoint === 'create' && !selectedOrg) return true
    if (selectedEndpoint === 'execute' && !(entityId ?? getEntityIdInput) && !selectedWorkflow) return true
    if (selectedEndpoint === 'execute-token' && (!selectedOrg || !selectedWorkflow)) return true
    if (selectedEndpoint === 'get-result' && (!(entityId ?? getEntityIdInput) || !(workflowExecutionId ?? getExecutionIdInput))) return true
    if (selectedEndpoint === 'get-entity' && !(entityId ?? getEntityIdInput)) return true
    return false
  }

  function runLabel(): string {
    const loadingKey: Record<string, string> = { lookup: 'lookup', create: 'createOrg', execute: 'execEntity', 'execute-token': 'execToken', 'get-result': 'result', 'get-entity': 'entity' }
    if (loading === loadingKey[selectedEndpoint]) return 'Loading…'
    const labels: Record<string, string> = {
      lookup: 'Search Registry',
      create: 'Create Organization',
      execute: 'Execute for Entity',
      'execute-token': 'Execute by Token',
      'get-result': 'Get Workflow Result',
      'get-entity': 'Get Entity',
    }
    return labels[selectedEndpoint] ?? 'Run'
  }

  const curlBody = isGetEndpoint ? undefined : (editorDirty ? (getEditorPayload() ?? undefined) : derivedPayload())

  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: '200px minmax(350px, 400px) 1fr', alignItems: 'start', padding: '18px 0' }}
    >
      {/* ── COLUMN 1: Endpoint Picker ── */}
      <EndpointPicker
        endpoints={KYB_V2_ENDPOINTS}
        selectedId={selectedEndpoint}
        onSelect={handleEndpointSelect}
      />

      {/* ── COLUMN 2: Form Panel ── */}
      <div className="rounded-[14px] overflow-hidden" style={{ background: '#111c30', border: '1px solid #1e2c44' }}>
        {/* Header strip */}
        <div className="flex items-center gap-2.5 px-4 py-3" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44' }}>
          <span
            className="px-2 py-0.5 rounded text-[10px] font-bold font-mono"
            style={epDef.method === 'GET'
              ? { background: 'rgba(96,165,250,.15)', color: '#60a5fa' }
              : { background: 'rgba(52,211,153,.13)', color: '#34d399' }}
          >
            {epDef.method}
          </span>
          <span className="text-[11px] font-mono text-[#8aa0c2] truncate flex-1">{epDef.path}</span>
        </div>

        <div className="p-4 space-y-4">
          {/* Connection + Workflow — always shown */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => void testConnection()}
              disabled={loading !== null || !hasCreds}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11px] font-medium transition-colors disabled:opacity-40"
              style={{ background: 'rgba(52,211,153,.1)', border: '1px solid rgba(52,211,153,.25)', color: '#34d399' }}
            >
              {loading === 'conn' ? <RefreshCw size={11} className="animate-spin" /> : connStatus === 'ok' ? <Wifi size={11} /> : connStatus === 'error' ? <WifiOff size={11} className="text-red-400" /> : <Wifi size={11} />}
              {loading === 'conn' ? 'Checking…' : connStatus === 'ok' ? 'Connected' : connStatus === 'error' ? 'Failed' : 'Test connection'}
            </button>
            <button
              onClick={() => void loadWorkflows()}
              disabled={loading !== null || !hasCreds}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11px] font-medium transition-colors disabled:opacity-40"
              style={{ background: '#16223a', border: '1px solid #283a57', color: '#c7d2fe' }}
            >
              {loading === 'wf' ? <RefreshCw size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              Workflows
            </button>
          </div>

          {/* Workflow selector — shown for execute, execute-token, get-result */}
          {(selectedEndpoint === 'execute' || selectedEndpoint === 'execute-token' || selectedEndpoint === 'get-result') && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Service Name</label>
                <input className={inputCls} value={kybServiceName} onChange={e => setKybServiceName(e.target.value)} placeholder="KYB" />
              </div>
              <div>
                <label className={labelCls}>Workflow Name</label>
                <ComboSelect
                  value={selectedWorkflow}
                  onChange={setSelectedWorkflow}
                  options={workflows.map(w => ({ value: w.workflowName }))}
                  placeholder="Select or type workflow name"
                  className={inputCls}
                />
              </div>
            </div>
          )}

          {/* Lookup endpoint */}
          {selectedEndpoint === 'lookup' && (
            <>
              <div>
                <SectionLabel>Test Scenarios</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                  {KYB_SCENARIOS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => { setScenarioIdx(i); applyScenario(i) }}
                      className="flex items-center gap-1.5 px-2.5 py-[5px] rounded-[8px] text-[11px] font-medium transition-all"
                      style={scenarioIdx === i
                        ? { background: 'rgba(99,102,241,.16)', border: '1px solid rgba(99,102,241,.45)', color: '#e2e8f0' }
                        : { background: '#0f1a2e', border: '1px solid #233149', color: '#94a3b8' }
                      }
                    >
                      {s.name.split(' ')[0]}
                      {s.tags.map(t => (
                        <span key={t} className="text-[9px] px-1 py-0.5 rounded" style={{ background: 'rgba(56,189,248,.1)', color: '#38bdf8' }}>{t}</span>
                      ))}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <SectionLabel>Business Lookup</SectionLabel>
                <div className="flex gap-3 mb-2 text-[10px]">
                  <label className="flex items-center gap-1.5 cursor-pointer text-[#94a3b8]">
                    <input type="radio" checked={searchBy === 'registration'} onChange={() => setSearchBy('registration')} style={{ accentColor: '#6366f1' }} />
                    By Registration
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer text-[#94a3b8]">
                    <input type="radio" checked={searchBy === 'name'} onChange={() => setSearchBy('name')} style={{ accentColor: '#6366f1' }} />
                    By Name
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {searchBy === 'name' ? (
                    <div className="col-span-2">
                      <label className={labelCls}>Organisation Name</label>
                      <input className={inputCls} value={searchName} onChange={e => setSearchName(e.target.value)} />
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className={labelCls}>Registration Number</label>
                        <input className={inputCls} value={searchRegNum} onChange={e => setSearchRegNum(e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Type</label>
                        <select className={inputCls} value={searchRegType} onChange={e => setSearchRegType(e.target.value)}>
                          <option>ABN</option><option>ACN</option><option>NZBN</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div>
                    <label className={labelCls}>Country</label>
                    <input className={inputCls} value={searchCountry} onChange={e => setSearchCountry(e.target.value)} />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Create endpoint */}
          {selectedEndpoint === 'create' && (
            <div>
              {selectedOrg ? (
                <div className="p-3 rounded-[9px]" style={{ background: 'rgba(99,102,241,.08)', border: '1px solid rgba(99,102,241,.3)' }}>
                  <p className="text-[10px] font-semibold text-[#e2e8f0] mb-1">{String(selectedOrg.name?.name ?? 'Selected Organization')}</p>
                  <p className="text-[9px] font-mono text-[#5b6b85] break-all">token: {String(selectedOrg.organizationToken ?? '—')}</p>
                </div>
              ) : (
                <p className="text-[10px] text-amber-400 italic">Select an organization from Lookup first</p>
              )}
            </div>
          )}

          {/* Execute endpoint */}
          {selectedEndpoint === 'execute' && (
            <div>
              <label className={labelCls}>Entity ID</label>
              <input
                className={inputCls}
                value={getEntityIdInput || entityId || ''}
                onChange={e => setGetEntityIdInput(e.target.value)}
                placeholder="entityId from create step"
              />
            </div>
          )}

          {/* Execute by Token endpoint */}
          {selectedEndpoint === 'execute-token' && (
            <div>
              {selectedOrg ? (
                <div className="p-3 rounded-[9px]" style={{ background: 'rgba(99,102,241,.08)', border: '1px solid rgba(99,102,241,.3)' }}>
                  <p className="text-[10px] font-semibold text-[#e2e8f0] mb-1">{String(selectedOrg.name?.name ?? 'Selected Organization')}</p>
                  <p className="text-[9px] font-mono text-[#5b6b85] break-all">token: {String(selectedOrg.organizationToken ?? '—')}</p>
                </div>
              ) : (
                <p className="text-[10px] text-amber-400 italic">Select an organization from Lookup first</p>
              )}
            </div>
          )}

          {/* Get Result endpoint */}
          {selectedEndpoint === 'get-result' && (
            <div className="space-y-2">
              <div>
                <label className={labelCls}>Entity ID</label>
                <input
                  className={inputCls}
                  value={getEntityIdInput}
                  onChange={e => setGetEntityIdInput(e.target.value)}
                  placeholder="entityId"
                />
              </div>
              <div>
                <label className={labelCls}>Execution ID</label>
                <input
                  className={inputCls}
                  value={getExecutionIdInput}
                  onChange={e => setGetExecutionIdInput(e.target.value)}
                  placeholder="workflowExecutionId"
                />
              </div>
            </div>
          )}

          {/* Get Entity endpoint */}
          {selectedEndpoint === 'get-entity' && (
            <div>
              <label className={labelCls}>Entity ID</label>
              <input
                className={inputCls}
                value={getEntityIdInput}
                onChange={e => setGetEntityIdInput(e.target.value)}
                placeholder="entityId"
              />
            </div>
          )}

          {/* KYB async note */}
          {(selectedEndpoint === 'execute' || selectedEndpoint === 'execute-token') && (
            <div className="text-[9px] text-amber-400 font-mono px-2 py-1 rounded" style={{ background: 'rgba(251,191,36,.06)', border: '1px solid rgba(251,191,36,.15)' }}>
              KYB workflows return 202 Accepted — switch to "Workflow Results" to poll for the final result
            </div>
          )}

          {/* entityId badge */}
          {entityId && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.2)' }}>
              <span className="text-[10px] font-semibold text-emerald-400">entityId</span>
              <span className="text-[10px] font-mono text-[#94a3b8] flex-1 truncate">{entityId}</span>
              <CopyButton text={entityId} />
              <button onClick={reset} className="text-[#5b6b85] hover:text-red-400 transition-colors"><X size={10} /></button>
            </div>
          )}

          {/* workflowExecutionId badge */}
          {workflowExecutionId && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(56,189,248,.06)', border: '1px solid rgba(56,189,248,.2)' }}>
              <span className="text-[10px] font-semibold text-sky-400">202</span>
              <span className="text-[9px] font-mono text-[#94a3b8] flex-1 truncate">{workflowExecutionId}</span>
            </div>
          )}

          {/* Run button */}
          {!hasCreds && (
            <p className="text-[10px] text-amber-400 text-center">Configure credentials above to make live calls</p>
          )}
          <button
            onClick={handleRun}
            disabled={runDisabled()}
            className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
            style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
          >
            {loading !== null ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</span> : runLabel()}
          </button>
        </div>
      </div>

      {/* ── COLUMN 3: Results ── */}
      <div className="space-y-3 min-w-0">
        {/* PayloadEditor + cURL — above webhooks */}
        {!isGetEndpoint && (
          <PayloadEditor
            value={editorText}
            dirty={editorDirty}
            error={editorError}
            onChange={handleEditorChange}
            onReset={resetEditor}
          />
        )}
        <CurlCollapsible
          method={epDef.method}
          url={fullUrl}
          creds={creds}
          body={curlBody}
        />

        <InlineWebhookPanel entityId={entityId} customerId={creds.customerId || null} />

        {/* Lookup results */}
        {selectedEndpoint === 'lookup' && lookupResults.length > 0 && (
          <div className="space-y-1.5">
            <SectionLabel>Lookup Results — click to select</SectionLabel>
            {lookupResults.map((org, i) => {
              const abn = org.registrationDetails?.find((r: { registrationNumberType: string }) => r.registrationNumberType === 'ABN')?.registrationNumber as string | undefined
              const isSelected = selectedOrg?.organizationToken === org.organizationToken
              return (
                <div
                  key={i}
                  className="rounded-[9px] p-2.5 cursor-pointer transition-all"
                  style={isSelected
                    ? { border: '1px solid rgba(99,102,241,.4)', background: 'rgba(99,102,241,.1)' }
                    : { border: '1px solid #233149', background: '#0a1322' }
                  }
                  onClick={() => setSelectedOrg(org)}
                >
                  <p className="text-[11px] font-semibold text-[#e2e8f0]">{String(org.name?.name ?? '—')}</p>
                  <div className="flex gap-3 text-[9px] text-[#5b6b85] mt-0.5 flex-wrap">
                    {abn && <span>ABN: {abn}</span>}
                    {org.status?.normalized?.code && <span>Status: {String(org.status.normalized.code)}</span>}
                    {org.type?.description && <span>{String(org.type.description)}</span>}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {!workflowResult && !entityResult && lookupResults.length === 0 && (
          <div
            className="flex flex-col items-center justify-center py-16 rounded-[14px]"
            style={{ border: '2px dashed #233149', background: '#0d1626' }}
          >
            <div className="w-12 h-12 rounded-[12px] flex items-center justify-center mb-4"
              style={{ background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.2)' }}>
              <Zap size={22} className="text-[#6366f1]" />
            </div>
            <p className="text-[14px] font-semibold text-[#5b6b85]">KYB Workspace</p>
            <p className="text-[11px] text-[#3f4f6b] mt-1.5 text-center max-w-xs">
              Search for a business, select it, create the entity and execute a KYB workflow
            </p>
          </div>
        )}

        {(loading === 'execEntity' || loading === 'execToken') ? (
          <div className="p-5 rounded-[14px] space-y-3" style={{ background: '#0e1828', border: '1px solid #2a3754' }}>
            <div className="flex items-center gap-3">
              <Loader2 size={18} className="animate-spin text-amber-400 shrink-0" />
              <p className="text-[13px] font-semibold text-[#fcd34d]">Executing workflow…</p>
            </div>
          </div>
        ) : null}

        {workflowResult && (
          <div className="space-y-3" style={{ animation: 'fadeUp .25s ease' }}>
            <div className="p-4 rounded-[14px]" style={{ background: oc.bg, border: `1px solid ${oc.border}` }}>
              <div className="flex items-start gap-4">
                <div className="w-[46px] h-[46px] rounded-[12px] flex items-center justify-center shrink-0"
                  style={{ background: `${oc.icon}20`, border: `1px solid ${oc.border}` }}>
                  {outcome === 'PASS' && <Check size={22} style={{ color: oc.icon }} />}
                  {outcome === 'FAIL' && <X size={22} style={{ color: oc.icon }} />}
                  {outcome === 'REFER' && <AlertTriangle size={22} style={{ color: oc.icon }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[19px] font-extrabold" style={{ color: oc.text }}>{outcome || 'DONE'}</span>
                  {resultOrg && (
                    <p className="text-[11px] text-[#94a3b8] mt-1 font-mono">
                      {String(resultOrg?.details?.name?.name ?? resultOrg?.name?.name ?? '')}
                    </p>
                  )}
                  {entityId && (
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] text-[#5b6b85]">entityId</span>
                      <span className="text-[10px] font-mono text-[#94a3b8] truncate">{entityId}</span>
                      <CopyButton text={entityId} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <RawJsonCollapsible data={fullResult} />
          </div>
        )}

        {Boolean(entityResult) && (
          <div style={{ animation: 'fadeUp .25s ease' }}>
            <EntityResultPanel entityResult={entityResult} />
          </div>
        )}
      </div>
    </div>
  )
}
